import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const BASE = "/admin/fg-backfill";
const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const err = (e) => toast.error(e?.response?.data?.detail || e?.response?.data?.error || e.message || "Request failed");

const Card = ({ label, value, tone = "text-white", testid }) => (
  <div className="border border-[var(--border)] bg-[var(--bg-1)] rounded-sm p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className={`mt-1 font-mono text-lg ${tone}`}>{value}</div>
  </div>
);

function EnvBanner({ st }) {
  return (
    <div data-testid="fgbf-env-banner" className="flex flex-wrap gap-4 items-center border border-amber-500/40 bg-amber-500/10 rounded-sm px-4 py-2 text-xs font-mono">
      <span>Environment: <b className="text-white" data-testid="fgbf-env-host">{window.location.host}</b></span>
      <span>Database: <b className="text-white" data-testid="fgbf-env-db">{st.database}</b></span>
      <span data-testid="fgbf-posting-state" className={st.execute_enabled ? "text-red-300" : "text-emerald-300"}>
        Posting: <b>{st.execute_enabled ? "ENABLED" : "DISABLED (dry-run only)"}</b>
      </span>
      <span data-testid="fgbf-pending-recovery" className={st.pending_recovery?.length ? "text-amber-300" : "text-zinc-400"}>
        Recovery pending: <b>{(st.pending_recovery || []).map(h => h.dc_no).join(", ") || "none"}</b>
      </span>
    </div>
  );
}

function Summary({ m }) {
  const s = m.summary;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fgbf-summary">
      <Card label="Missing documents" value={`${s.missing_total} · ${fmt(s.missing_qty)} m`} testid="fgbf-sum-missing" />
      <Card label="Ready (DC / EDC)" value={`${s.ready} (${s.ready_dc} / ${s.ready_edc})`} tone="text-emerald-300" testid="fgbf-sum-ready" />
      <Card label="Exceptions" value={s.exceptions} tone={s.exceptions ? "text-red-300" : "text-white"} testid="fgbf-sum-exceptions" />
      <Card label="Receipts to reconstruct" value={`${fmt(s.receipt_qty)} m`} testid="fgbf-sum-receipt" />
      <Card label="Dispatch OUT · net effect" value={`${fmt(s.out_qty)} · ${fmt(s.net_stock_effect)} m`} testid="fgbf-sum-out" />
    </div>
  );
}

function DocTable({ docs, sel, setSel, frozen }) {
  return (
    <div className="border border-[var(--border)] rounded-sm overflow-auto max-h-[420px]" data-testid="fgbf-doc-table">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[var(--bg-2)] text-zinc-400 uppercase tracking-wider text-[10px]">
          <tr>{["", "Type", "DC/EDC", "Delivery", "Status", "Delivered", "OUT", "Reconstruct", "Receipt dates / source", "Exceptions"].map(h => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
        </thead>
        <tbody>
          {docs.map(d => (
            <tr key={d.dc_id} className="border-t border-[var(--border)]" data-testid={`fgbf-row-${d.dc_no}`}>
              <td className="px-2 py-1.5">{d.status === "READY" && frozen && (
                <Checkbox data-testid={`fgbf-select-${d.dc_no}`} checked={sel.includes(d.dc_id)}
                  onCheckedChange={(v) => setSel(v ? [...sel, d.dc_id].slice(0, 25) : sel.filter(x => x !== d.dc_id))} />)}</td>
              <td className="px-2">{d.type}</td>
              <td className="px-2 font-mono">{d.dc_no}</td>
              <td className="px-2 font-mono">{d.delivery_date}</td>
              <td className={`px-2 font-mono ${d.status === "READY" ? "text-emerald-300" : d.status === "EXCEPTION" ? "text-red-300" : "text-zinc-400"}`}>{d.status}</td>
              <td className="px-2 text-right font-mono">{fmt(d.delivered_qty)}</td>
              <td className="px-2 text-right font-mono">{fmt(d.out_qty)}</td>
              <td className="px-2 text-right font-mono">{fmt(d.reconstruct_qty)}</td>
              <td className="px-2 text-zinc-400 max-w-[260px]">{[...new Set((d.packs || []).filter(p => p.receipt_action === "RECONSTRUCT").map(p => `${p.receipt_date} · ${p.date_source}`))].join("; ") || "—"}</td>
              <td className="px-2 text-red-300 max-w-[320px] whitespace-normal">{(d.exceptions || []).join(" | ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BatchEffect({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="border border-[var(--border)] rounded-sm overflow-auto max-h-[260px]" data-testid="fgbf-batch-effect">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[var(--bg-2)] text-zinc-400 uppercase tracking-wider text-[10px]">
          <tr>{["Product Code", "Batch", "Receipt +", "Dispatch −", "Net"].map(h => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
        </thead>
        <tbody>{rows.map(r => (
          <tr key={`${r.product_code}|${r.batch}`} className="border-t border-[var(--border)] font-mono">
            <td className="px-2 py-1">{r.product_code}</td><td className="px-2">{r.batch}</td>
            <td className="px-2 text-right">{fmt(r.receipt)}</td><td className="px-2 text-right">{fmt(r.out)}</td>
            <td className={`px-2 text-right ${r.receipt - r.out < 0 ? "text-red-300" : ""}`}>{fmt(r.receipt - r.out)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function Results({ log }) {
  if (!log.length) return null;
  return (
    <div className="border border-[var(--border)] rounded-sm overflow-auto max-h-[260px]" data-testid="fgbf-log">
      <table className="w-full text-xs"><tbody>
        {log.map((x, i) => (
          <tr key={i} className="border-t border-[var(--border)]">
            <td className="px-2 py-1 font-mono">{x.at?.slice(0, 19)}</td><td className="px-2 font-mono">{x.dc_no || x.dc_id}</td>
            <td className={`px-2 font-mono ${String(x.result).includes("POSTED") ? "text-emerald-300" : "text-amber-300"}`}>{x.result}</td>
            <td className="px-2 text-zinc-400">{x.reason || x.error || (x.receipts || []).join(", ")}</td>
          </tr>))}
      </tbody></table>
    </div>
  );
}

export default function FgBackfill() {
  const [st, setSt] = useState(null);
  const [m, setM] = useState(null);
  const [hash, setHash] = useState("");
  const [exported, setExported] = useState(false);
  const [backup, setBackup] = useState(false);
  const [sel, setSel] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get(`${BASE}/status`), api.get(`${BASE}/log`)]);
      setSt(s.data); setLog(l.data);
    } catch (e) { setSt({ error: e?.response?.status }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const run = (k, fn) => async () => { setBusy(k); try { await fn(); } catch (e) { err(e); } finally { setBusy(""); } };
  const dryRun = run("dry", async () => { const { data } = await api.get(`${BASE}/dry-run`); setM(data); setHash(""); setExported(false); setSel([]); });
  const freeze = run("freeze", async () => { const { data } = await api.post(`${BASE}/freeze`); setHash(data.manifest_hash); toast.success(`Manifest frozen · ${data.manifest_hash}`); });
  const download = (path, name, type) => run(name, async () => {
    const { data } = await api.get(path, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([data], { type })); a.download = name; a.click();
  });
  const exportScoped = run("export", async () => {
    const { data } = await api.get(`${BASE}/export/${hash}`);
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: "application/json" }));
    a.download = `fg-backfill-scoped-export-${hash}.json`; a.click(); setExported(true);
  });
  const execute = run("exec", async () => {
    const { data } = await api.post(`${BASE}/execute`, { manifest_hash: hash, dc_ids: sel, backup_confirmed: backup });
    if (!data.ok) { toast.error(data.error || data.code); return; }
    const posted = data.results.filter(r => String(r.result).includes("POSTED")).length;
    toast.success(`Batch done · ${posted} posted · ${data.results.length - posted} skipped/failed`);
    setSel([]); await load(); const { data: d2 } = await api.get(`${BASE}/dry-run`); setM(d2);
  });
  const ready = useMemo(() => (m?.documents || []).filter(d => d.status === "READY"), [m]);

  if (!st) return <div className="p-6 text-zinc-400">Loading…</div>;
  if (st.error) return <div className="p-6 text-zinc-400" data-testid="fgbf-unavailable">FG backfill is not available ({st.error}).</div>;
  return (
    <div className="p-6 space-y-5" data-testid="fg-backfill-page">
      <div>
        <h1 className="text-2xl font-semibold text-white">FG Historical Backfill</h1>
        <p className="text-sm text-zinc-400 mt-1">Reconstructs missing FG receipts and posts missing dispatches for delivered DC/EDCs through the canonical stock services.</p>
      </div>
      <EnvBanner st={st} />
      <div className="flex flex-wrap gap-2">
        <Button data-testid="fgbf-dry-run-btn" onClick={dryRun} disabled={!!busy}>{busy === "dry" ? "Building…" : "1 · Dry run"}</Button>
        <Button data-testid="fgbf-download-manifest-btn" variant="outline" disabled={!m || !!busy}
          onClick={download(`${BASE}/dry-run.csv`, "fg-backfill-manifest.csv", "text/csv")}>Download manifest (CSV)</Button>
        <Button data-testid="fgbf-freeze-btn" variant="outline" disabled={!m || !!busy} onClick={freeze}>2 · Freeze manifest</Button>
        <Button data-testid="fgbf-export-btn" variant="outline" disabled={!hash || !!busy} onClick={exportScoped}>3 · Scoped export</Button>
      </div>
      {hash && <div className="font-mono text-sm" data-testid="fgbf-manifest-hash">Frozen manifest: <b className="text-amber-300">{hash}</b>{exported && <span className="text-emerald-300"> · scoped export downloaded</span>}</div>}
      {m && <Summary m={m} />}
      {m && <DocTable docs={m.documents.filter(d => d.status !== "ALREADY_SATISFIED")} sel={sel} setSel={setSel} frozen={!!hash} />}
      {m && <div className="text-xs uppercase tracking-widest text-zinc-500">Expected stock effect by product and batch (ready documents)</div>}
      {m && <BatchEffect rows={m.stock_effect_by_batch} />}
      {m && hash && (
        <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-4 space-y-3" data-testid="fgbf-exec-panel">
          <label className="flex items-center gap-2 text-sm text-zinc-200">
            <Checkbox data-testid="fgbf-backup-confirm" checked={backup} onCheckedChange={(v) => setBackup(!!v)} />
            I confirm a recoverable Atlas snapshot exists for this environment.
          </label>
          <div className="flex gap-2 items-center">
            <Button data-testid="fgbf-select-next-btn" variant="outline" onClick={() => setSel(ready.slice(0, 25).map(d => d.dc_id))}>Select next 25 ready</Button>
            <Button data-testid="fgbf-execute-btn" className="bg-red-600 hover:bg-red-500"
              disabled={!st.execute_enabled || !backup || !exported || !sel.length || !!busy} onClick={execute}>
              {busy === "exec" ? "Posting…" : `4 · Post batch (${sel.length})`}
            </Button>
            {!st.execute_enabled && <span className="text-xs text-zinc-400" data-testid="fgbf-exec-disabled-note">Posting is disabled (FG_BACKFILL_EXECUTE_ENABLED is off).</span>}
          </div>
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-zinc-500">Resume log · committed and skipped results ({log.length})</div>
      <Results log={log} />
    </div>
  );
}
